import { create } from 'zustand';
import { supabase } from '../integrations/supabase/client';
import { mockCategoryStats } from '../data/MockCategoryStats';

/**
 * 用于管理分类统计数据的Store
 */
const useCategoryStatsStore = create((set, get) => ({
  // 状态
  stats: [],
  isLoading: false,
  error: null,
  connectionStatus: '未测试',
  useMockData: false,
  isDevelopment: import.meta.env.DEV || import.meta.env.MODE === 'development',
  debugInfo: null, // 调试信息字段
  lastFetchTime: null, // 上次获取数据的时间
  cacheExpiry: 5 * 60 * 1000, // 缓存过期时间（毫秒），默认5分钟
  
  // 设置缓存过期时间
  setCacheExpiry: (timeInMs) => {
    set({ cacheExpiry: timeInMs });
  },
  
  // 切换使用真实/模拟数据
  toggleMockData: (value) => {
    const newValue = typeof value === 'boolean' ? value : !get().useMockData;
    set({ useMockData: newValue });
    
    // 如果切换到模拟数据，立即加载模拟数据
    if (newValue) {
      set({ stats: mockCategoryStats, isLoading: false, error: null });
    } else {
      // 如果切换到真实数据，尊重缓存机制（不强制刷新）
      get().fetchCategoryStats(); 
    }
  },
  
  // 测试Supabase连接
  testConnection: async () => {
    try {
      const { data, error } = await supabase.from('feed').select('feed_id').limit(1);
      
      if (error) {
        console.error('数据库连接测试失败:', error);
        set({ connectionStatus: `连接失败: ${error.message}` });
        return false;
      }
      
      console.log('数据库连接测试成功:', data);
      set({ connectionStatus: '连接成功' });
      return true;
    } catch (err) {
      console.error('数据库连接测试异常:', err);
      set({ connectionStatus: `连接异常: ${err.message}` });
      return false;
    }
  },
  
  // 清除调试信息
  clearDebugInfo: () => {
    set({ debugInfo: null });
  },
  
  // 清除缓存
  clearCache: () => {
    set({ lastFetchTime: null });
    console.log('分类统计数据缓存已清除');
  },
  
  // 检查是否需要刷新数据
  needsRefresh: () => {
    const { lastFetchTime, cacheExpiry } = get();
    
    // 如果没有上次获取时间，需要刷新
    if (!lastFetchTime) return true;
    
    // 计算时间差
    const now = new Date().getTime();
    const timeDiff = now - lastFetchTime;
    
    // 如果超过缓存过期时间，需要刷新
    return timeDiff > cacheExpiry;
  },
  
  // 获取文章计数的直接方法
  getArticleCountsForAllFeeds: async () => {
    try {
      // 1. 首先获取所有文章
      const { data: allArticles, error: articleError } = await supabase
        .from('article')
        .select('article_id, feed_id');
        
      if (articleError) {
        console.error('获取所有文章失败:', articleError);
        return {};
      }
      
      // 2. 在内存中进行计数
      const feedCounts = {};
      if (allArticles) {
        allArticles.forEach(article => {
          if (article.feed_id) {
            feedCounts[article.feed_id] = (feedCounts[article.feed_id] || 0) + 1;
          }
        });
      }
      
      console.log('文章计数结果:', feedCounts);
      return feedCounts;
    } catch (err) {
      console.error('获取文章计数出错:', err);
      return {};
    }
  },
  
  // 获取分类统计数据
  fetchCategoryStats: async (forceMock = false, forceRefresh = false) => {
    // 检查是否使用缓存
    const needsRefresh = forceRefresh || get().needsRefresh();
    
    if (!needsRefresh && get().stats.length > 0) {
      console.log('使用缓存的分类统计数据，跳过加载');
      return;
    }
    
    const debugData = {}; // 收集调试信息
    
    // 如果离线或强制使用模拟数据
    if (!navigator.onLine || get().useMockData || forceMock) {
      console.log('使用模拟数据');
      set({ 
        stats: mockCategoryStats, 
        isLoading: false, 
        error: null, 
        lastFetchTime: new Date().getTime() 
      });
      return;
    }

    set({ isLoading: true, error: null, debugInfo: null });
    
    try {
      // 测试连接
      const isConnected = await get().testConnection();
      debugData.connectionTest = isConnected;
      
      if (!isConnected) {
        throw new Error('数据库连接失败，请检查配置');
      }
      
      // 先获取所有文章的计数 - 使用直接方法
      console.log('获取所有文章的计数...');
      const allArticleCounts = await get().getArticleCountsForAllFeeds();
      debugData.allArticleCounts = allArticleCounts;
      
      console.log('开始获取分类数据...');
      // 获取所有分类
      const { data: categories, error: catError } = await supabase
        .from('feed')
        .select('category')
        .neq('category', null)
        .order('category', { ascending: true });
      
      if (catError) {
        debugData.categoryError = catError;
        throw catError;
      }
      
      debugData.rawCategories = categories;
      console.log('获取的原始分类数据:', categories);
      
      if (!categories || categories.length === 0) {
        console.log('没有找到分类数据');
        set({ 
          stats: [], 
          isLoading: false,
          debugInfo: { ...debugData, error: '没有找到分类数据' },
          lastFetchTime: new Date().getTime()
        });
        return;
      }
      
      // 去重分类数据
      const uniqueCategories = Array.from(new Set(categories.map(c => c.category)))
        .map(cat => ({ category: cat }))
        .filter(c => c.category);
      
      debugData.uniqueCategories = uniqueCategories;
      console.log('处理后的唯一分类:', uniqueCategories);
      
      // 获取每个分类下的 feed 数据
      console.log('开始获取各分类的feed数据...');
      const categoryStats = [];
      
      for (const cat of uniqueCategories) {
        console.log(`获取分类 ${cat.category} 的feeds...`);
        const { data: feeds, error: feedError } = await supabase
          .from('feed')
          .select('feed_id, title, category')
          .eq('category', cat.category);
        
        if (feedError) {
          console.error(`获取分类 ${cat.category} 的feeds失败:`, feedError);
          debugData.feedError = { category: cat.category, error: feedError };
          throw feedError;
        }
        
        console.log(`分类 ${cat.category} 获取到 ${feeds?.length || 0} 个feeds`);
        
        if (!feeds || feeds.length === 0) {
          categoryStats.push({
            category: cat.category,
            feeds: []
          });
          continue;
        }
        
        // 处理每个feed的文章计数 - 使用预先获取的计数
        const feedStats = feeds.map(feed => {
          // 从预先获取的计数中获取，如果不存在则为0
          const count = allArticleCounts[feed.feed_id] || 0;
          console.log(`Feed ${feed.feed_id} (${feed.title || 'untitled'}) 的文章数量: ${count}`);
          
          return {
            feedId: feed.feed_id,
            feedName: feed.title || `Feed ${feed.feed_id}`,
            articleCount: count
          };
        });
        
        categoryStats.push({
          category: cat.category,
          feeds: feedStats
        });
      }
      
      console.log('最终处理的统计数据:', categoryStats);
      debugData.finalStats = categoryStats;
      
      // 统计是否有任何文章
      const hasAnyArticles = categoryStats.some(cat => 
        cat.feeds && cat.feeds.some(feed => feed.articleCount > 0)
      );
      debugData.hasAnyArticles = hasAnyArticles;
      
      set({ 
        stats: categoryStats, 
        isLoading: false, 
        error: null,
        debugInfo: debugData,
        lastFetchTime: new Date().getTime() // 更新数据获取时间
      });
    } catch (err) {
      console.error('获取统计数据失败:', err);
      set({ 
        error: err.message, 
        isLoading: false,
        debugInfo: { ...debugData, finalError: err.toString() }
      });
      
      // 如果出错且在开发环境，可以自动切换到模拟数据
      if (get().isDevelopment) {
        console.log('切换到模拟数据...');
        set({ 
          stats: mockCategoryStats,
          lastFetchTime: new Date().getTime() // 即使是模拟数据也更新获取时间
        });
      }
    }
  }
}));

export default useCategoryStatsStore; 